/**
 * Lazy Loading Manager
 * Gerencia o carregamento assíncrono de imagens, vídeos e componentes
 */

class LazyLoadManager {
    constructor(options = {}) {
        this.options = {
            threshold: options.threshold || 0.1,
            rootMargin: options.rootMargin || '50px',
            imageClass: options.imageClass || 'lazy-image',
            videoClass: options.videoClass || 'lazy-video',
            ...options
        };

        this.imageObserver = null;
        this.videoObserver = null;
        this.elementObserver = null;
        this.init();
    }

    init() {
        this.optimizeImageLoading();

        // Criar observers
        this.createImageObserver();
        this.createVideoObserver();
        this.createElementObserver();

        // Observar elementos na página
        this.observeAllLazyElements();

        // Event listeners para novo conteúdo dinamicamente carregado
        document.addEventListener('contentLoaded', () => this.observeAllLazyElements());
    }

    /**
     * Aplicar estratégias nativas de carregamento otimizado para imagens
     */
    optimizeImageLoading() {
        const viewportHeight = window.innerHeight || 900;

        document.querySelectorAll('img').forEach((img) => {
            const rect = img.getBoundingClientRect();
            const isAboveFold = rect.top < (viewportHeight * 1.2);
            const hasExplicitPriority = img.dataset.priority === 'high';

            if (isAboveFold || hasExplicitPriority) {
                if (!img.hasAttribute('loading')) {
                    img.setAttribute('loading', 'eager');
                }
                img.setAttribute('fetchpriority', 'high');
                img.setAttribute('decoding', 'sync');
            } else {
                if (!img.hasAttribute('loading')) {
                    img.setAttribute('loading', 'lazy');
                }
                if (!img.hasAttribute('fetchpriority')) {
                    img.setAttribute('fetchpriority', 'low');
                }
                if (!img.hasAttribute('decoding')) {
                    img.setAttribute('decoding', 'async');
                }
            }
        });
    }

    /**
     * Observer para imagens lazy loading
     */
    createImageObserver() {
        this.imageObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.loadImage(entry.target);
                }
            });
        }, {
            threshold: this.options.threshold,
            rootMargin: this.options.rootMargin
        });
    }

    /**
     * Observer para vídeos lazy loading
     */
    createVideoObserver() {
        this.videoObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.loadVideo(entry.target);
                }
            });
        }, {
            threshold: 0.05,
            rootMargin: '100px'
        });
    }

    /**
     * Observer genérico para elementos com data-lazy-src
     */
    createElementObserver() {
        this.elementObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && entry.target.dataset.lazySrc) {
                    this.loadElement(entry.target);
                }
            });
        }, {
            threshold: 0.1,
            rootMargin: '50px'
        });
    }

    /**
     * Observar todos os elementos lazy na página
     */
    observeAllLazyElements() {
        // Imagens
        document.querySelectorAll(`img[data-src], img.${this.options.imageClass}`).forEach(img => {
            if (!img.src || img.src.includes('data:')) {
                this.imageObserver.observe(img);
            }
        });

        // Vídeos
        document.querySelectorAll(`video[data-src], video.${this.options.videoClass}`).forEach(video => {
            this.videoObserver.observe(video);
        });

        // Elementos genéricos
        document.querySelectorAll('[data-lazy-src]').forEach(el => {
            this.elementObserver.observe(el);
        });

        // iFrames
        document.querySelectorAll('iframe[data-src]').forEach(iframe => {
            this.elementObserver.observe(iframe);
        });
    }

    /**
     * Carregar imagem
     */
    loadImage(img) {
        const src = img.dataset.src;
        if (!src) return;

        // Criar nova imagem para pré-carregar
        const newImg = new Image();

        newImg.onload = () => {
            img.src = src;
            img.classList.add('loaded');
            delete img.dataset.src;
            this.imageObserver.unobserve(img);
        };

        newImg.onerror = () => {
            console.warn(`Falha ao carregar imagem: ${src}`);
            img.classList.add('error');
            this.imageObserver.unobserve(img);
        };

        newImg.src = src;

        // Suporte a srcset
        if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
            delete img.dataset.srcset;
        }
    }

    /**
     * Carregar vídeo
     */
    loadVideo(video) {
        const src = video.dataset.src;
        if (!src) return;

        const source = document.createElement('source');
        source.src = src;
        source.type = video.dataset.type || 'video/mp4';

        video.appendChild(source);
        video.classList.add('loaded');
        delete video.dataset.src;

        // Carregar poster se houver
        if (video.dataset.poster) {
            video.poster = video.dataset.poster;
            delete video.dataset.poster;
        }

        this.videoObserver.unobserve(video);
    }

    /**
     * Carregar elemento genérico (iframe, etc)
     */
    loadElement(el) {
        const src = el.dataset.lazySrc;
        if (!src) return;

        el.src = src;

        el.classList.add('loaded');
        delete el.dataset.lazySrc;
        this.elementObserver.unobserve(el);
    }

    /**
     * Forçar carregamento imediato de um elemento
     */
    loadImmediately(element) {
        if (element.tagName === 'IMG') {
            this.loadImage(element);
        } else if (element.tagName === 'VIDEO') {
            this.loadVideo(element);
        } else {
            this.loadElement(element);
        }
    }

    /**
     * Destruir observers
     */
    destroy() {
        this.imageObserver?.disconnect();
        this.videoObserver?.disconnect();
        this.elementObserver?.disconnect();
    }
}

// Inicializar quando o DOM está pronto
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        globalThis.lazyLoadManager = new LazyLoadManager();
    });
} else {
    globalThis.lazyLoadManager = new LazyLoadManager();
}
